import React, { useState, useCallback, useEffect } from "react";
import { Dialog, DialogContent, Slide, useTheme } from "@material-ui/core";
import useMediaQuery from '@material-ui/core/useMediaQuery';

// Transition component for dialog
const Transition = React.forwardRef(function Transition(props, ref) {
    return <Slide direction="left" ref={ref} {...props} />;
});

const DEFAULT_IFRAME_ALLOW = "publickey-credentials-get; clipboard-read; clipboard-write *";
const OTP_IFRAME_ALLOW = "publickey-credentials-get; clipboard-read; clipboard-write; otp-credentials";

/**
 * @component IframeDialog
 * @description Reusable dialog component for iframe-based modals
 * @param {boolean} open - Controls dialog visibility
 * @param {string} frameUrl - URL for the iframe
 * @param {Function} onClose - Callback when iframe sends completion message
 * @param {string} iframeId - Unique ID for the iframe element
 * @param {string} title - Title attribute for the iframe
 * @param {boolean} [allowOtpCredentials] - Grant otp-credentials permission for recovery phone flows
 */
const IframeDialog = ({open, frameUrl, onClose, iframeId, title, allowOtpCredentials = false}) => {
    const [loaded, setLoaded] = useState(false);
    const theme = useTheme();
    const loadHandler = useCallback(() => setLoaded(true), []);
    const closeHandler = useCallback(() => onClose(null), [onClose]);
    const fullScreen = useMediaQuery(theme.breakpoints.down('xs'));
    const iframeAllow = allowOtpCredentials ? OTP_IFRAME_ALLOW : DEFAULT_IFRAME_ALLOW;
    const shouldRenderIframe = open && frameUrl;

    useEffect(() => {
        setLoaded(false);
    }, [frameUrl, allowOtpCredentials]);

    useEffect(() => {
        let listener = null;

        if (loaded && shouldRenderIframe) {
            const iframeElement = document.getElementById(iframeId);
            const childWindow = iframeElement?.contentWindow;

            if (!childWindow) {
                return undefined;
            }

            listener = message => {
                if (message.source !== childWindow) {
                    return; // Skip message in this event listener
                }

                if (message.data.status === "complete") {
                    if (onClose) {
                        onClose(message.data)
                    }
                }
            };

            window.addEventListener('message', listener, false);
        }

        return () => {
            if (listener) {
                window.removeEventListener('message', listener, false);
            }
        };
    }, [loaded, iframeId, onClose, shouldRenderIframe]);

    return (
        <Dialog
            open={open}
            TransitionComponent={Transition}
            keepMounted={!allowOtpCredentials}
            onClose={closeHandler}
            aria-labelledby={`${iframeId}-dialog-title`}
            aria-describedby={`${iframeId}-dialog-description`}
            fullWidth={true}
            className="accountFrameDialog"
            fullScreen={fullScreen}
            disableBackdropClick={true}
            disableEscapeKeyDown={true}
        >
            <DialogContent>
                {shouldRenderIframe && (
                    <iframe
                        key={`${iframeId}-${frameUrl}`}
                        id={iframeId}
                        onLoad={loadHandler}
                        allow={iframeAllow}
                        src={frameUrl}
                        title={title}
                    />
                )}
            </DialogContent>
        </Dialog>
    );
};

export default IframeDialog;
